import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { CheckCircle, ClipboardCheck, Building2, Users, ArrowRight, FileText, Eye, MapPin, HelpCircle } from 'lucide-react';

const faqs = [
  {
    question: "What is institutional accreditation by IMCAC?",
    answer: "Institutional accreditation by IMCAC is a quality assurance process that evaluates training academies, medical education programs, and examination centers against established competency-based standards. It validates the quality of training programs without conferring degree-granting status."
  },
  {
    question: "How long does the accreditation process take?",
    answer: "The accreditation process typically takes 3-6 months from initial application to final decision. This includes documentary review, on-site evaluation, faculty interviews, and committee deliberation. Complex applications may require additional time."
  },
  {
    question: "What are the eligibility requirements for institutional accreditation?",
    answer: "Institutions must demonstrate: qualified faculty with relevant clinical experience, adequate infrastructure and equipment, structured curriculum aligned with IMCAC frameworks, sufficient clinical training volume, proper governance and ethical standards, and established quality assurance processes."
  },
  {
    question: "How long is the accreditation valid?",
    answer: "Initial accreditation is typically granted for 3-5 years, depending on the institution's compliance level. Accredited institutions must undergo periodic reviews and submit annual reports to maintain their accredited status."
  },
  {
    question: "What is the difference between institutional accreditation and examination center approval?",
    answer: "Institutional accreditation validates training programs and curricula, while examination center approval certifies facilities for conducting IMCAC competency examinations. An institution may hold both, but they require separate applications and evaluations."
  },
  {
    question: "Can international institutions apply for IMCAC accreditation?",
    answer: "Yes, IMCAC accreditation is open to institutions globally. International applicants follow the same standards and process, with accommodations made for virtual preliminary reviews where necessary before on-site evaluations."
  }
];

export default function AccreditationPage() {
  return (
    <div data-testid="accreditation-page">
      <section className="bg-[#1B2A4A] py-16 md:py-20">
        <div className="imcac-container">
          <p className="imcac-overline mb-3">Division A</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight font-heading">Division of Institutional Accreditation</h1>
          <div className="h-1 w-20 bg-[#C5A572] mt-5" />
          <p className="text-lg text-slate-300 mt-4 max-w-2xl">
            Accredits training academies, medical education programs, and examination centers meeting IMCAC quality standards.
          </p>
        </div>
      </section>

      <section className="imcac-section bg-white">
        <div className="imcac-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <h2 className="text-3xl font-semibold text-[#1B2A4A] font-heading mb-6">Program Accreditation Standards</h2>
              <p className="text-base text-slate-600 leading-relaxed mb-6">
                The Division of Institutional Accreditation evaluates and accredits skill-based medical programs that meet 
                IMCAC standards. This accreditation validates the quality of training without conferring degree-granting status.
              </p>

              <h3 className="text-xl font-semibold text-[#1B2A4A] font-heading mb-4">Accreditation Criteria</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {[
                  { icon: FileText, title: 'Curriculum Audit', desc: 'Comprehensive review of program content against IMCAC competency frameworks' },
                  { icon: Users, title: 'Faculty Validation', desc: 'Assessment of teaching staff qualifications, experience, and ongoing development' },
                  { icon: Building2, title: 'Infrastructure Review', desc: 'Evaluation of facilities, equipment, and clinical training environments' },
                  { icon: ClipboardCheck, title: 'Clinical Adequacy', desc: 'Verification of case volume, supervision ratios, and patient exposure' },
                  { icon: Eye, title: 'Ethics Compliance', desc: 'Review of ethical standards, patient consent processes, and governance' },
                  { icon: CheckCircle, title: 'Outcome Assessment', desc: 'Analysis of graduate competency and program effectiveness metrics' },
                ].map((item, i) => (
                  <Card key={i} className="imcac-card">
                    <CardContent className="p-4">
                      <item.icon className="h-5 w-5 text-[#C5A572] mb-2" />
                      <h4 className="text-sm font-semibold text-[#1B2A4A]">{item.title}</h4>
                      <p className="text-xs text-slate-500 mt-1">{item.desc}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/apply/institutional" data-testid="accreditation-contact-btn">
                  <Button className="bg-[#1B2A4A] hover:bg-[#152039] text-white rounded-sm">
                    <Building2 className="mr-2 h-4 w-4" /> Apply for Institution Accreditation
                  </Button>
                </Link>
                <Link to="/apply/examination-center" data-testid="exam-center-apply-btn">
                  <Button variant="outline" className="border-[#C5A572] text-[#C5A572] hover:bg-[#C5A572] hover:text-white rounded-sm">
                    <MapPin className="mr-2 h-4 w-4" /> Apply as Examination Center
                  </Button>
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <img
                src="https://images.unsplash.com/photo-1758573467240-f944226c2026?auto=format&fit=crop&w=800&q=80"
                alt="Research facility"
                className="w-full h-64 object-cover rounded-sm shadow-md mb-6"
              />
              <Card className="imcac-card border-t-2 border-t-[#C5A572]">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-[#1B2A4A] font-heading mb-3">Accreditation Process</h3>
                  <ol className="space-y-3">
                    {[
                      'Submit institutional application and self-assessment',
                      'Documentary review by Accreditation Committee',
                      'On-site evaluation by IMCAC review panel',
                      'Faculty and student interviews',
                      'Committee deliberation and decision',
                      'Accreditation granted (with conditions if applicable)',
                    ].map((step, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-slate-700">
                        <span className="w-6 h-6 bg-[#F1F5F9] border border-slate-200 rounded-sm flex items-center justify-center text-xs font-bold text-[#1B2A4A] flex-shrink-0">{i + 1}</span>
                        {step}
                      </li>
                    ))}
                  </ol>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="imcac-section bg-slate-50">
        <div className="imcac-container max-w-4xl">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#1B2A4A]/5 rounded-sm mb-3">
              <HelpCircle className="h-3 w-3 text-[#C5A572]" />
              <span className="text-xs font-semibold tracking-wider text-[#1B2A4A] uppercase">FAQs</span>
            </div>
            <h2 className="text-2xl font-bold text-[#1B2A4A] font-heading">Frequently Asked Questions</h2>
          </div>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="border-b border-slate-200">
                <AccordionTrigger className="text-left font-semibold text-[#1B2A4A] hover:text-[#C5A572] hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
}
